import { FC } from 'react'
import { AssetsModule } from '@/core/modules/digital-footprint/assets/assets.module'
import { IntegrationsStore } from '@/core/store/Integrations/integrations.store'
import { formatDate } from '@/core/utils/time'
import Typography from '@/ui/components/typography'
import { DetailsGrid } from '@ui/drawer/common/DetailsGrid'
import { InfoItem } from '@ui/drawer/common/infoItem'
import { PLACEHOLDER } from '../assetInfoDomainDetails/assetInfoDomainDetails.utils'
import { SubSectionTitle, UserWrapper } from './assetUserInfo.styles'

type TAssetInfoProps = {
    asset: TAsset
}

const AssetUserInfo: FC<TAssetInfoProps> = ({ asset }) => {
    const {
        hostname,
        department,
        userType,
        firstName,
        lastName,
        jobTitle,
        mobilePhone,
        mfaEnabled,
        mfaType,
        lastPasswordChangeAt,
        passwordPolicy,
        integrationId,
    } = asset

    const cloudApps = AssetsModule.getCloudApps()
    const matchedCloudApp = integrationId
        ? cloudApps.find(
              (cloudApp) => cloudApp.integration_id === integrationId
          )
        : undefined

    const integrationName = integrationId
        ? IntegrationsStore.getIntegrationByIntegrationId(integrationId)?.name
        : undefined

    const accountValue = matchedCloudApp?.hostname || integrationName

    const displayName =
        firstName && lastName ? `${firstName} ${lastName}` : undefined

    const mfaEnabledText =
        typeof mfaEnabled === 'boolean' ? (mfaEnabled ? 'Yes' : 'No') : null

    const showBusinessNetworkInfo =
        mfaEnabledText || mfaType || lastPasswordChangeAt || passwordPolicy

    return (
        <UserWrapper>
            <DetailsGrid dataTestId="user-details-grid">
                <InfoItem title="Email Address" dataTestId="email-address">
                    {hostname || PLACEHOLDER}
                </InfoItem>
                <InfoItem title="Account" dataTestId="vendor">
                    {accountValue || PLACEHOLDER}
                </InfoItem>
                <InfoItem title="Display Name" dataTestId="display-name">
                    {displayName || PLACEHOLDER}
                </InfoItem>
                <InfoItem title="First Name" dataTestId="first-name">
                    {firstName || PLACEHOLDER}
                </InfoItem>
                <InfoItem title="Last Name" dataTestId="last-name">
                    {lastName || PLACEHOLDER}
                </InfoItem>
                <InfoItem title="User Type" dataTestId="user-type">
                    {userType || PLACEHOLDER}
                </InfoItem>
                {jobTitle && (
                    <InfoItem title="Job Title" dataTestId="job-title">
                        {jobTitle}
                    </InfoItem>
                )}
                {department && (
                    <InfoItem title="Department" dataTestId="department">
                        {department}
                    </InfoItem>
                )}
                {mobilePhone && (
                    <InfoItem title="Mobile Phone" dataTestId="mobile-phone">
                        {mobilePhone}
                    </InfoItem>
                )}
            </DetailsGrid>
            {showBusinessNetworkInfo && (
                <>
                    <SubSectionTitle>
                        <Typography variant="l-regular" component="h3">
                            Business Network Information
                        </Typography>
                    </SubSectionTitle>
                    <DetailsGrid dataTestId="business-network-grid">
                        {mfaEnabledText && (
                            <InfoItem
                                title="MFA Enabled"
                                dataTestId="mfa-enabled"
                            >
                                {mfaEnabledText}
                            </InfoItem>
                        )}
                        {mfaType && (
                            <InfoItem title="MFA Type" dataTestId="mfa-type">
                                {mfaType}
                            </InfoItem>
                        )}
                        {lastPasswordChangeAt && (
                            <InfoItem
                                title="Last Password Change At"
                                dataTestId="last-password-change-at"
                            >
                                {formatDate(lastPasswordChangeAt)}
                            </InfoItem>
                        )}
                        {passwordPolicy && (
                            <InfoItem
                                title="Password Policy"
                                dataTestId="password-policy"
                            >
                                {passwordPolicy}
                            </InfoItem>
                        )}
                    </DetailsGrid>
                </>
            )}
        </UserWrapper>
    )
}

export default AssetUserInfo
